import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Options, JOB_STATUS_CODE } from 'types';
import { FormError } from 'components';
import { Filter16, ZoomInArea16, Close16 } from '@carbon/icons-react';
import {
  DatePicker,
  DatePickerInput,
  Button,
  Form,
  TextInput,
  Checkbox,
  Dropdown,
  SelectItem,
  TimePicker,
  TimePickerSelect
} from 'carbon-components-react';
import { useFormik } from 'formik';
import { SearchReferralsSchema } from 'utilities/validation';
import Utils from 'utilities/helper.util';
import { union, without, includes } from 'lodash';
import AddressSearch from '../../../enquiryHistory/components/SearchFilter/addressSearch';

const enquiryLodgedOption: Options = [
  {
    label: 'Today (default)',
    value: 'today'
  },
  {
    label: 'Last 7 days',
    value: '7'
  },
  {
    label: 'Last 30 days',
    value: '30'
  },
  {
    label: 'Custom range ...',
    value: 'el-custom'
  }
];

const lastUpdatedOption: Options = [
  {
    label: 'Today',
    value: 'lu-today'
  },
  {
    label: 'Last 7 days',
    value: 'lu-7'
  },
  {
    label: 'Last 30 days',
    value: 'lu-30'
  },
  {
    label: 'Custom range ...',
    value: 'lu-custom'
  }
];

const jobStartOption: Options = [
  {
    label: 'Next 7 days',
    value: 'n-7'
  },
  {
    label: 'Next 30 days',
    value: 'n-30'
  },
  {
    label: 'Next 90 days',
    value: 'n-90'
  },
  {
    label: 'Last 7 days',
    value: 'l-7'
  },
  {
    label: 'Last 30 days',
    value: 'l-30'
  },
  {
    label: 'Last 90 days',
    value: 'l-90'
  },
  {
    label: 'Custom range ...',
    value: 'js-custom'
  }
];

const getDateOptionItem = (type: string, value: string) => {
  if (value === 'last-30-days') {
    value = '30';
  } else if (value === 'last-7-days' || value === 'this-week') {
    value = '7';
  }

  if (type === 'enquiryLodged') {
    return enquiryLodgedOption.find((o) => value === o.value);
  } else if (type === 'lastUpdated') {
    return lastUpdatedOption.find((o) => value === o.value);
  } else {
    return jobStartOption.find((o) => value === o.value);
  }
};

const ApplyFilterForm: React.FC<Props> = ({
  handleFilter,
  filterValues,
  resetValues,
  setFilterValues,
  handleOpenCloseFilter,
  parentOverflow,
  isListView,
  disabledAllExceptApply,
  rangeText
}: any) => {
  const mapSearchWithExtentZoomRef = useRef(false);

  const {
    touched,
    errors,
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    resetForm,
    setFieldValue
  } = useFormik({
    enableReinitialize: true,
    initialValues: filterValues,
    validationSchema: SearchReferralsSchema,
    onSubmit: (values: any) => {
      handleFilter(values, mapSearchWithExtentZoomRef.current);
    }
  });

  const handleResetFilter = () => {
    resetValues();
    resetForm();
  };

  const updateFilterValues = useCallback(
    (updatedValues: object) => {
      setFilterValues({
        ...values,
        ...updatedValues
      });
    },
    [values, setFilterValues]
  );

  const handleDropdownChange = ({ selectedItem }: any) => {
    if (selectedItem?.value) {
      switch (selectedItem.value) {
        case 'today': {
          const today = Utils.getNewDate().startOf('day');
          updateFilterValues({
            enquiryLodged: selectedItem.value,
            createdAfter: today,
            createdBefore: undefined
          });
          break;
        }
        case '7':
        case '30': {
          const date = Utils.getDateBeforeDays(
            Number(selectedItem.value) - 1
          ).startOf('day');
          updateFilterValues({
            enquiryLodged: selectedItem.value,
            createdAfter: date,
            createdBefore: undefined
          });
          break;
        }
        case 'el-custom': {
          updateFilterValues({
            enquiryLodged: selectedItem.value,
            createdAfter: undefined,
            createdBefore: undefined,
            hourCreatedAfter: undefined,
            hourCreatedBefore: undefined,
            amPmCreatedAfter: undefined,
            amPmCreatedBefore: undefined
          });
          break;
        }

        case 'lu-today': {
          const today = Utils.getNewDate().startOf('day');
          updateFilterValues({
            lastUpdated: selectedItem.value,
            updatedAfter: today,
            updatedBefore: undefined
          });
          break;
        }
        case 'lu-7':
        case 'lu-30': {
          const days = selectedItem.value.split('-');
          const date = Utils.getDateBeforeDays(Number(days[1]) - 1).startOf(
            'day'
          );
          updateFilterValues({
            lastUpdated: selectedItem.value,
            updatedAfter: date,
            updatedBefore: undefined
          });
          break;
        }
        case 'lu-custom': {
          updateFilterValues({
            lastUpdated: selectedItem.value,
            updatedAfter: undefined,
            updatedBefore: undefined,
            hourUpdatedAfter: undefined,
            hourUpdatedBefore: undefined,
            amPmUpdatedAfter: undefined,
            amPmUpdatedBefore: undefined
          });
          break;
        }

        case 'n-7':
        case 'n-30':
        case 'n-90': {
          const days = selectedItem.value.split('-');
          const date = Utils.getDateAfterDays(Number(days[1])).startOf('day');
          const today = Utils.getNewDate().startOf('day');
          updateFilterValues({
            jobStart: selectedItem.value,
            jobStartCustomed: false,
            digStartAfter: today,
            digStartBefore: date
          });
          break;
        }
        case 'l-7':
        case 'l-30':
        case 'l-90': {
          const days = selectedItem.value.split('-');
          const date = Utils.getDateBeforeDays(Number(days[1]) - 1).startOf(
            'day'
          );
          const today = Utils.getNewDate().startOf('day');
          updateFilterValues({
            jobStart: selectedItem.value,
            jobStartCustomed: false,
            digStartAfter: date,
            digStartBefore: today
          });
          break;
        }
        case 'js-custom': {
          updateFilterValues({
            jobStart: selectedItem.value,
            jobStartCustomed: false,
            digStartAfter: undefined,
            digStartBefore: undefined
          });
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  const handleStatusChange = (value: string, checked: boolean) => {
    const { WAITING, CREATED } = JOB_STATUS_CODE;
    let { status } = values;
    if (checked) {
      const updateStatus = value === WAITING ? [value, CREATED] : [value];
      status = union(status, updateStatus);
    } else {
      status =
        value === WAITING
          ? without(status, value, CREATED)
          : without(status, value);
    }
    updateFilterValues({
      status
    });
  };

  const handleDateChange = (key: string, value: any) => {
    if (Array.isArray(value) && value.length > 0) {
      const updatedValue = value[0];
      updateFilterValues({
        [key]: Utils.getNewDate(updatedValue)
      });
    }
  };

  const handleTimeChange = (key: string, val: any) => {
    const updatedValue = val?.[0];

    if (updatedValue) {
      const updateDatas = {
        [key]: Utils.getNewDate(updatedValue),
        ...(key === 'updatedAfter' && {
          hourUpdatedAfter: values?.hourUpdatedAfter || '12:00',
          amPmUpdatedAfter: values?.amPmUpdatedAfter || 'AM'
        }),
        ...(key === 'updatedBefore' && {
          hourUpdatedBefore: values?.hourUpdatedBefore || '11:59',
          amPmUpdatedBefore: values?.amPmUpdatedBefore || 'PM'
        }),
        ...(key === 'createdAfter' && {
          hourCreatedAfter: values?.hourCreatedAfter || '12:00',
          amPmCreatedAfter: values?.amPmCreatedAfter || 'AM'
        }),
        ...(key === 'createdBefore' && {
          hourCreatedBefore: values?.hourCreatedBefore || '11:59',
          amPmCreatedBefore: values?.amPmCreatedBefore || 'PM'
        })
      };
      updateFilterValues(updateDatas);

      // For each key in updateDatas, set the corresponding form field value
      Object.entries(updateDatas).forEach(([field, value]) => {
        setFieldValue(field, value, true);
      });
    }
  };

  const translateOptionLabel = (type: string, item: any) => {
    const { digStartAfter, digStartBefore, jobStartCustomed } = values;
    if (type === 'enquiryLodged' || type === 'lastUpdated') {
      return item.label;
    } else {
      return item.value === 'js-custom' && jobStartCustomed
        ? `${Utils.toLocalDateFormat(
            digStartAfter
          )} – ${Utils.toLocalDateFormat(digStartBefore)}`
        : item.label;
    }
  };

  const onChangeDate = (key: string, val: any, setField?: boolean) => {
    setField && setFieldValue(key, Utils.getNewDate(val[0]), true);
    handleDateChange(key, val);
  };

  const onCalendarClose = (val: any, strValue: any, self: any) => {
    strValue && self.setDate(strValue, true, 'd/m/Y');
  };

  const onSelectAddress = (item: any) => {
    if (window.loadedView && item.center && item.zoom) {
      window.loadedView.goTo({
        center: item.center,
        zoom: item.zoom
      });
    }
  };

  useEffect(() => {
    const {
      digStartAfter,
      digStartBefore,
      jobStart,
      jobStartCustomed
    } = values;
    if (
      jobStart === 'js-custom' &&
      digStartAfter &&
      digStartBefore &&
      !jobStartCustomed
    ) {
      updateFilterValues({
        jobStartCustomed: true
      });
    }
  }, [values, updateFilterValues]);

  return (
    <div
      className={`search-filter-panel-wrapper ${
        parentOverflow ? 'parent-overflow' : ''
      }`}
    >
      <div
        className={'search-filter-panel-button'}
        onClick={
          disabledAllExceptApply
            ? undefined
            : () => handleOpenCloseFilter(false)
        }
      >
        <Filter16 />
      </div>
      <section className="search-filter-panel-main">
        <Form>
          <div className="search-filter-panel-form-wrapper">
            <div className="heading">
              <p>Filter the view</p>
              <Button
                kind="ghost"
                size="sm"
                onClick={
                  disabledAllExceptApply
                    ? (event: any) => {
                        mapSearchWithExtentZoomRef.current = false;
                        handleSubmit(event);
                      }
                    : () => handleOpenCloseFilter(false)
                }
              >
                <Close16 />
              </Button>
            </div>
            {!isListView && (
              <div>
                <AddressSearch
                  onClickResult={(item: any) => onSelectAddress(item)}
                />
              </div>
            )}
            <div className="search-filter-panel-date-container">
              <div className="dropdown-container">
                <Dropdown
                  ariaLabel="Enquiry lodged"
                  id="enquiry-lodged-date"
                  items={enquiryLodgedOption}
                  label="Choose an option"
                  titleText="Enquiry lodged"
                  onChange={handleDropdownChange}
                  itemToString={(item) =>
                    translateOptionLabel('enquiryLodged', item)
                  }
                  initialSelectedItem={getDateOptionItem(
                    'enquiryLodged',
                    rangeText ? rangeText : values.enquiryLodged
                  )}
                />
                {values.enquiryLodged === 'el-custom' && (
                  <>
                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      value={
                        values.createdAfter
                          ? Utils.toLocalDateFormat(values.createdAfter)
                          : ''
                      }
                      onChange={(val: any) => {
                        handleTimeChange('createdAfter', val);
                      }}
                      onClose={onCalendarClose}
                    >
                      <DatePickerInput
                        id="createdAfter"
                        name="createdAfter"
                        data-testid="createdAfter"
                        labelText="From"
                        placeholder="dd/mm/yyyy"
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                    <TimePicker
                      id="hourCreatedAfter"
                      pattern="(1[012]|[1-9]):[0-5][0-9](\\s)?"
                      placeholder="h:mm"
                      onChange={handleChange}
                      onBlur={handleChange}
                      disabled={!values.createdAfter}
                      value={values.hourCreatedAfter}
                    >
                      <TimePickerSelect
                        id="amPmCreatedAfter"
                        labelText="time-format-picker-createdAfter"
                        disabled={
                          !values.createdAfter && !values.hourCreatedAfter
                        }
                        value={values.amPmCreatedAfter}
                        onChange={handleChange}
                        onBlur={handleChange}
                      >
                        <SelectItem value="AM" text="AM" />
                        <SelectItem value="PM" text="PM" />
                      </TimePickerSelect>
                    </TimePicker>
                    {touched && errors.hourCreatedAfter && (
                      <FormError>{errors.hourCreatedAfter}</FormError>
                    )}

                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      onChange={(val: any) => {
                        handleTimeChange('createdBefore', val);
                      }}
                      onClose={onCalendarClose}
                      value={
                        values.createdBefore
                          ? Utils.toLocalDateFormat(values.createdBefore)
                          : ''
                      }
                    >
                      <DatePickerInput
                        id="createdBefore"
                        name="createdBefore"
                        labelText="To"
                        data-testid="createdBefore"
                        placeholder="dd/mm/yyyy"
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                    <TimePicker
                      id="hourCreatedBefore"
                      pattern="(1[012]|[1-9]):[0-5][0-9](\\s)?"
                      placeholder="h:mm"
                      onChange={handleChange}
                      onBlur={handleChange}
                      disabled={!values.createdBefore}
                      value={values.hourCreatedBefore}
                    >
                      <TimePickerSelect
                        id="amPmCreatedBefore"
                        labelText="time-format-picker-createdBefore"
                        disabled={
                          !values.createdBefore && !values.hourCreatedBefore
                        }
                        value={values.amPmCreatedBefore}
                        onChange={handleChange}
                        onBlur={handleChange}
                      >
                        <SelectItem value="AM" text="AM" />
                        <SelectItem value="PM" text="PM" />
                      </TimePickerSelect>
                    </TimePicker>
                    {touched && errors.hourCreatedBefore && (
                      <FormError>{errors.hourCreatedBefore}</FormError>
                    )}
                    {touched && errors.createdBefore && (
                      <FormError>{errors.createdBefore}</FormError>
                    )}
                  </>
                )}
              </div>
              <div className="dropdown-container">
                <Dropdown
                  ariaLabel="Last updated"
                  id="enquiry-lodged-date"
                  items={lastUpdatedOption}
                  label="Choose an option"
                  titleText="Last updated"
                  onChange={handleDropdownChange}
                  itemToString={(item) =>
                    translateOptionLabel('lastUpdated', item)
                  }
                  initialSelectedItem={getDateOptionItem(
                    'lastUpdated',
                    rangeText ? rangeText : values.lastUpdated
                  )}
                />
                {values.lastUpdated === 'lu-custom' && (
                  <>
                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      value={
                        values.updatedAfter
                          ? Utils.toLocalDateFormat(values.updatedAfter)
                          : ''
                      }
                      onChange={(val: any) =>
                        handleTimeChange('updatedAfter', val)
                      }
                      onClose={onCalendarClose}
                    >
                      <DatePickerInput
                        id="updatedAfter"
                        name="updatedAfter"
                        data-testid="updatedAfter"
                        labelText="From"
                        placeholder="dd/mm/yyyy"
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                    <TimePicker
                      id="hourUpdatedAfter"
                      pattern="(1[012]|[1-9]):[0-5][0-9](\\s)?"
                      placeholder="h:mm"
                      onChange={handleChange}
                      onBlur={handleChange}
                      disabled={!values.updatedAfter}
                      value={values.hourUpdatedAfter}
                    >
                      <TimePickerSelect
                        id="amPmUpdatedAfter"
                        labelText="time-format-picker-updatedAfter"
                        disabled={
                          !values.updatedAfter && !values.hourUpdatedAfter
                        }
                        value={values.amPmUpdatedAfter}
                        onChange={handleChange}
                        onBlur={handleChange}
                      >
                        <SelectItem value="AM" text="AM" />
                        <SelectItem value="PM" text="PM" />
                      </TimePickerSelect>
                    </TimePicker>
                    {touched && errors.hourUpdatedAfter && (
                      <FormError>{errors.hourUpdatedAfter}</FormError>
                    )}

                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      onChange={(val: any) =>
                        handleTimeChange('updatedBefore', val)
                      }
                      onClose={onCalendarClose}
                      value={
                        values.updatedBefore
                          ? Utils.toLocalDateFormat(values.updatedBefore)
                          : ''
                      }
                    >
                      <DatePickerInput
                        id="updatedBefore"
                        name="updatedBefore"
                        labelText="To"
                        data-testid="updatedBefore"
                        placeholder="dd/mm/yyyy"
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                    <TimePicker
                      id="hourUpdatedBefore"
                      pattern="(1[012]|[1-9]):[0-5][0-9](\\s)?"
                      placeholder="h:mm"
                      onChange={handleChange}
                      onBlur={handleChange}
                      disabled={!values.updatedBefore}
                      value={values.hourUpdatedBefore}
                    >
                      <TimePickerSelect
                        id="amPmUpdatedBefore"
                        labelText="time-format-picker-updatedBefore"
                        disabled={
                          !values.updatedBefore && !values.hourUpdatedBefore
                        }
                        value={values.amPmUpdatedBefore}
                        onChange={handleChange}
                        onBlur={handleChange}
                      >
                        <SelectItem value="AM" text="AM" />
                        <SelectItem value="PM" text="PM" />
                      </TimePickerSelect>
                    </TimePicker>
                    {touched && errors.hourUpdatedBefore && (
                      <FormError>{errors.hourUpdatedBefore}</FormError>
                    )}
                    {touched && errors.updatedBefore && (
                      <FormError>{errors.updatedBefore}</FormError>
                    )}
                  </>
                )}
              </div>
              <div className="dropdown-container">
                <Dropdown
                  ariaLabel="Job start dates"
                  id="job-start-date"
                  items={jobStartOption}
                  label="Choose an option"
                  titleText="Job start dates"
                  onChange={handleDropdownChange}
                  itemToString={(item) =>
                    translateOptionLabel('jobStart', item)
                  }
                  initialSelectedItem={getDateOptionItem(
                    'jobStart',
                    values.jobStart
                  )}
                />
                {values.jobStart === 'js-custom' && !values.jobStartCustomed && (
                  <div className="date-picker-container">
                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      onChange={(val: any) =>
                        onChangeDate('digStartAfter', val, false)
                      }
                      onClose={onCalendarClose}
                      value={
                        values.digStartAfter
                          ? Utils.toLocalDateFormat(values.digStartAfter)
                          : ''
                      }
                    >
                      <DatePickerInput
                        id="digStartAfter"
                        name="digStartAfter"
                        data-testid="digStartAfter"
                        labelText="From"
                        placeholder="dd/mm/yyyy"
                        size="sm"
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      onChange={(val: any) =>
                        onChangeDate('digStartBefore', val, true)
                      }
                      onClose={onCalendarClose}
                      value={
                        values.digStartBefore
                          ? Utils.toLocalDateFormat(values.digStartBefore)
                          : ''
                      }
                    >
                      <DatePickerInput
                        id="digStartBefore"
                        name="digStartBefore"
                        labelText="To"
                        data-testid="digStartBefore"
                        placeholder="dd/mm/yyyy"
                        size="sm"
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                  </div>
                )}
              </div>
              {touched && errors.digStartBefore && (
                <FormError>{errors.digStartBefore}</FormError>
              )}
            </div>
            <hr className="divider" />
            <div className="bx--fieldset status-checkbox-container">
              <legend className="bx--label">Status</legend>
              <Checkbox
                labelText="Waiting for response"
                id="status-checked-waiting"
                onChange={(checked: boolean) =>
                  handleStatusChange(JOB_STATUS_CODE.WAITING, checked)
                }
                checked={includes(values.status, JOB_STATUS_CODE.WAITING)}
              />
              <Checkbox
                labelText="Response received"
                id="status-checked-received"
                onChange={(checked: boolean) =>
                  handleStatusChange(JOB_STATUS_CODE.COMPLETED, checked)
                }
                checked={includes(values.status, JOB_STATUS_CODE.COMPLETED)}
              />
            </div>
            <hr className="divider" />
            <div className="search-filter-panel-input-container">
              <div className="filter-input">
                <TextInput
                  id="jobNumber"
                  name="jobNumber"
                  labelText="Job number"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.jobNumber}
                  data-testid="jobNumber"
                />
                {touched && errors.jobNumber && (
                  <FormError>{errors.jobNumber}</FormError>
                )}
              </div>
              <div className="filter-input">
                <TextInput
                  id="referralId"
                  name="referralId"
                  labelText="Referral ID"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.referralId}
                  data-testid="referralId"
                />
                {touched && errors.referralId && (
                  <FormError>{errors.referralId}</FormError>
                )}
              </div>
              <div className="filter-input">
                <TextInput
                  id="requestorFullNameContains"
                  name="requestorFullName"
                  labelText="Enquirer name contains"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.requestorFullName}
                  data-testid="requestorFullNameContains"
                />
                {touched && errors.requestorFullName && (
                  <FormError>{errors.requestorFullName}</FormError>
                )}
              </div>
              <div className="filter-input">
                <TextInput
                  id="requestorEmailContains"
                  name="requestorEmail"
                  labelText="Enquirer email contains"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.requestorEmail}
                  data-testid="requestorEmailContains"
                />
                {touched && errors.requestorEmail && (
                  <FormError>{errors.requestorEmail}</FormError>
                )}
              </div>
            </div>
          </div>
          <div className="search-filter-panel-form-button-container">
            <Button
              kind="ghost"
              size="field"
              onClick={disabledAllExceptApply ? undefined : handleResetFilter}
              disabled={disabledAllExceptApply}
            >
              Reset
            </Button>
            {!isListView && (
              <Button
                className="search-filter-panel-form-button-apply-zoom"
                size="field"
                disabled={
                  Object.keys(errors).length > 0 || disabledAllExceptApply
                }
                onClick={
                  disabledAllExceptApply
                    ? undefined
                    : (event: any) => {
                        mapSearchWithExtentZoomRef.current = true;
                        handleSubmit(event);
                      }
                }
              >
                <ZoomInArea16 />
              </Button>
            )}
            <Button
              size="field"
              disabled={Object.keys(errors).length > 0}
              onClick={(event: any) => {
                mapSearchWithExtentZoomRef.current = false;
                handleSubmit(event);
              }}
            >
              Apply
            </Button>
          </div>
        </Form>
      </section>
    </div>
  );
};

const propTypes = {
  handleFilter: PropTypes.func.isRequired,
  filterValues: PropTypes.object.isRequired,
  resetValues: PropTypes.func.isRequired,
  setFilterValues: PropTypes.func.isRequired,
  handleOpenCloseFilter: PropTypes.func.isRequired,
  parentOverflow: PropTypes.bool,
  isListView: PropTypes.bool,
  disabledAllExceptApply: PropTypes.bool,
  rangeText: PropTypes.string
};

type Props = PropTypes.InferProps<typeof propTypes>;

export default ApplyFilterForm;
